"use client";

import React, { createContext, ReactNode, RefObject, useContext, useMemo, useReducer, useRef } from "react";
import { Actions, reducer, State, initialState } from "./reducer";
import { usePathname } from "next/navigation";
import { normalizeBrandId, stepsUtilData } from "@/lib/utils";
import { useKeycloakSession } from "@/hooks/useKeycloakSession";
import { dCommBrands, BrandId, ROLES } from "@/constants/constants";

import { ErrorResponse } from "@/types";
import { useSavedWork } from "@/hooks/useSavedWork";
import { useErrorDispatcher } from "@/hooks/useErrorDispatcher";
import { useNavigationGuards } from "@/hooks/useNavigationGuards";

export type AppContextType = {
  state: State;
  dispatch: React.Dispatch<Actions>;
  dispatchErrorState: (err: ErrorResponse) => void;
  cancelTriggeredRef: RefObject<boolean>;
  cancelWithoutSavingRef: RefObject<HTMLElement | null>;
};
export const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const pathname = usePathname();
  const { session } = useKeycloakSession() ?? {};

  const cancelTriggeredRef = useRef(false);
  const cancelWithoutSavingRef = useRef<HTMLElement | null>(null);

  const brandId = normalizeBrandId(session?.selectedBrand?.id ?? "") as BrandId;
  const brandWithLocation = session?.selectedBrand?.id ?? "";
  const isNotOreo = !dCommBrands.includes(brandId);
  const isAdmin = session?.aidaADGroup === ROLES.ADMIN;
  const hasSession = Boolean(session);

  stepsUtilData.brandId = brandId;

  useSavedWork({
    brandId,
    brandWithLocation,
    isNotOreo,
    isAdmin,
    hasSession,
    dispatch,
    state,
    pathname,
  });

  const dispatchErrorState = useErrorDispatcher(dispatch);

  useNavigationGuards({
    state,
    dispatch,
    cancelTriggeredRef,
  });

  const contextValue = useMemo(
    () => ({ state, dispatch, dispatchErrorState, cancelTriggeredRef, cancelWithoutSavingRef }),
    [state, dispatch, dispatchErrorState, cancelTriggeredRef, cancelWithoutSavingRef],
  );

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
